import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { FaSpinner } from 'react-icons/fa';
import { probeYardTier1 } from './probeYardTier1';
import MapLibreGLLayer from './MapLibreGLLayer';
import LegacyViewBadge from './LegacyViewBadge';
import UnavailablePlaceholder from './UnavailablePlaceholder';

const MAPTILER_URL = `https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_API_KEY}`;
const MAPTILER_ATTRIBUTION =
  '&copy; <a href="https://www.maptiler.com/">MapTiler</a> &copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';

/**
 * @param {{ yard: { Yard: number, lat: number, lng: number, City: string, State: string } }} props
 */
export default function YardLayoutMap({ yard }) {
  const [tier, setTier] = useState('probing');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();

    probeYardTier1(yard.Yard, { signal: controller.signal }).then((available) => {
      if (!mountedRef.current) return;
      setTier(available ? 'tier1' : 'tier2');
    });

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [yard.Yard]);

  const center = [yard.lat, yard.lng];
  const tier1StyleUrl = `https://yardimages.copart.com/styles/yard${yard.Yard}-lite/style.json`;

  if (tier === 'probing') {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading yard layout...</p>
        </div>
      </div>
    );
  }

  if (tier === 'tier3') {
    return <UnavailablePlaceholder yard={yard} />;
  }

  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={center}
        zoom={18}
        scrollWheelZoom
        zoomControl
        className="w-full h-full"
        aria-label={`Yard ${yard.Yard} layout map`}
      >
        {tier === 'tier1' ? (
          <MapLibreGLLayer styleUrl={tier1StyleUrl} />
        ) : (
          <TileLayer url={MAPTILER_URL} attribution={MAPTILER_ATTRIBUTION} />
        )}
      </MapContainer>
      {tier === 'tier2' && <LegacyViewBadge />}
    </div>
  );
}
